import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, Star, Trash, Share, Check } from "@phosphor-icons/react";

/**
 * Reusable saved-views widget for Leads / Bad-Leads / Dashboard.
 * Props:
 *   viewType: "leads" | "bad_leads" | "dashboard"
 *   currentFilters: object — the filter state to save
 *   onApply(filters): called when the user picks a saved view
 */
export default function SavedViewsBar({ viewType, currentFilters, onApply }) {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [showSave, setShowSave] = useState(false);
  const [share, setShare] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/saved-views?view_type=${viewType}`);
    setRows(data.views);
  };
  useEffect(() => { load(); }, [viewType]);

  const save = async () => {
    if (!name) return toast.error("Name required");
    try {
      await api.post("/saved-views", {
        name, view_type: viewType, filters: currentFilters,
        is_default: isDefault, share_with_org: share,
      });
      toast.success("View saved");
      setName(""); setShowSave(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this saved view?")) return;
    await api.delete(`/saved-views/${id}`);
    toast.success("Deleted"); load();
  };

  const setDefault = async (v) => {
    await api.patch(`/saved-views/${v.id}`, { is_default: !v.is_default });
    load();
  };

  return (
    <div className="brut-card p-3 flex items-center gap-2 flex-wrap" data-testid={`saved-views-${viewType}`}>
      <div className="label-mono">Saved views</div>
      <div className="flex flex-wrap gap-1">
        {rows.map((v) => (
          <div key={v.id} className="inline-flex items-center border border-black" data-testid={`sv-${v.id}`}>
            <button
              onClick={() => onApply?.(v.filters)}
              data-testid={`sv-apply-${v.id}`}
              className="px-2 py-1 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
              {v.is_default && <Star size={10} weight="fill" className="inline mr-1" />}
              {v.name}
            </button>
            <button onClick={() => setDefault(v)} title="Toggle default"
              className="border-l border-black px-1 py-1 hover:bg-black hover:text-white">
              <Star size={10} weight={v.is_default ? "fill" : "regular"} />
            </button>
            <button onClick={() => remove(v.id)} title="Delete"
              className="border-l border-black px-1 py-1 hover:bg-red-600 hover:text-white">
              <Trash size={10} />
            </button>
          </div>
        ))}
      </div>
      {showSave ? (
        <div className="flex items-center gap-1" data-testid="sv-save-form">
          <input autoFocus data-testid="sv-name" placeholder="View name"
            value={name} onChange={(e) => setName(e.target.value)}
            className="border border-black px-2 py-1 text-xs w-40" />
          <label className="text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> default
          </label>
          <label className="text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
            <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} /> share org
          </label>
          <button onClick={save} data-testid="sv-save"
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
            <Check size={10} className="inline" /> Save
          </button>
          <button onClick={() => setShowSave(false)}
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest">✕</button>
        </div>
      ) : (
        <button onClick={() => setShowSave(true)} data-testid="sv-open"
          className="ml-auto border border-black px-2 py-1 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
          <Bookmark size={10} className="inline mr-1" /> Save current
        </button>
      )}
    </div>
  );
}
